import { useEffect, useMemo, useState } from 'react'
import { useEditor } from '../store/useEditor'
import { loadProject, type ProjectSummary } from '../lib/db'
import type { LightsOsDocument } from '../types/document'
import { sectionTotals, sectionsOf } from '../lib/sections'
import { currentApproval } from '../lib/approval'
import { bulbsForFeet, takeoff } from '../lib/materials'
import { PRODUCTS } from '../lib/products'
import { docNumber, longDate, normalizePaper } from '../lib/paper'
import { JOB_STATUS, type JobStatus } from '../types/document'

import JobOverview from './JobOverview'
import Sections from './Sections'

import Materials from './Materials'
import Packages from './Packages'
import Approval from './Approval'
import { Empty } from './bits'
import Estimate from './Estimate'
import RunInspector from './RunInspector'

/**
 * ONE JOB, FIVE STEPS, IN THE ORDER THE WORK HAPPENS.
 *
 * This is the v0.2 prototype's Design & Quote workspace, and its shape is the
 * whole point: 01 Smart Roof, 02 Mockup, 03 Materials, 04 Estimate, 05
 * Approved scope, along the top of one page, with the job's name above them.
 * A person walks left to right and the numbers say where they are.
 *
 * WHAT THIS REPLACES AND WHY. The same work was reachable only as a sheet that
 * opened over the dark editor, three tabs deep, once you had already loaded a
 * photograph. That put the measuring, the counts and the customer's yes behind
 * the drawing tool, when in the trade they come before it and after it.
 *
 * THE EDITOR IS NOT REPLACED. Step 02 is where the design lives and it opens
 * the real renderer, exactly as the prototype says: use the existing LightsOS
 * renderer for the finished customer image. Nothing about the drawing, the
 * night grade or the bulb look is touched by this file.
 */

/*
 * AND OVERVIEW IS THE FIRST OF THEM, BECAUSE THERE WAS ONLY EVER ONE JOB.
 *
 * The product carried two lists of houses. Jobs and Design & Quote rendered
 * the same rows, with the same thumbnails, the same filter strip, the same
 * states and the same amounts, under two names, and opening a house from one
 * gave the record while opening it from the other gave the five steps. Two
 * doors into one thing, and a person who wanted the measurements from the
 * record had to go back out and in through the other door.
 *
 * Design & Quote was never a place. It is what you do to a job. So it is a
 * strip of tabs on the job, the record is the first one, and the list of
 * houses is called Jobs and there is one of it.
 */
type Step = 'overview' | 'roof' | 'mockup' | 'materials' | 'estimate' | 'scope'

const STEPS: { id: Step; n: string; label: string }[] = [
  { id: 'overview', n: '', label: 'Overview' },
  { id: 'roof', n: '01', label: 'Smart Roof' },
  { id: 'mockup', n: '02', label: 'Mockup' },
  { id: 'materials', n: '03', label: 'Materials' },
  { id: 'estimate', n: '04', label: 'Estimate' },
  { id: 'scope', n: '05', label: 'Approved scope' },
]

/* The job's commercial state, said the same way the record says it. */
const statusLabel = (s: JobStatus) => JOB_STATUS.find((x) => x.id === s)?.label ?? 'Working on it'
const pillTone = (s: JobStatus) =>
  s === 'won' ? 'won' : s === 'lost' ? 'lost' : s === 'quoted' ? 'quoted' : ''

export default function DesignQuote({
  job,
  /** Which step to land on. The board sends a job to the one it means. */
  startOn = 'overview',
  onBack,
  onChanged,
  onOpenDesign,
  onOpenQuote,
}: {
  job: ProjectSummary
  startOn?: Step
  onBack: () => void

  onChanged: () => void
  onOpenDesign: (doc: LightsOsDocument) => void
  onOpenQuote: (doc: LightsOsDocument) => void
}) {
  const loadDocument = useEditor((s) => s.loadDocument)
  const prefix = useEditor((s) => s.company.prefix)
  const [doc, setDoc] = useState<LightsOsDocument | null>(null)

  const [missing, setMissing] = useState(false)
  const [step, setStep] = useState<Step>(startOn)



  useEffect(() => {
    let alive = true
    setDoc(null)
    setMissing(false)
    loadProject(job.id)
      .then((d) => {
        if (!alive) return
        if (d) {
          setDoc(d)
          // The panels below read the store, not this local copy, so the
          // document has to be the open one for any of them to work.
          loadDocument(d)
        } else setMissing(true)
      })
      .catch(() => alive && setMissing(true))
    return () => {
      alive = false
    }
  }, [job.id, loadDocument])

  const live = useEditor((s) => s.doc)
  const sections = sectionsOf(live)
  const totals = sectionTotals(sections)
  const approved = currentApproval(live)
  const product = PRODUCTS.C9
  const t = useMemo(() => takeoff(sections, product.spacingInches), [sections, product])
  const bulbs = t.lines.find((l) => l.key === 'bulbs')?.qty ?? 0

  const open = (then: (d: LightsOsDocument) => void) => {
    if (!doc) return
    loadDocument(doc)
    then(doc)
  }

  const who = job.customer.trim() || job.address.trim() || job.name
  const number = docNumber(normalizePaper(live.paper, live.quote ?? []), prefix)


  return (
    <>
      {/*
        ONE TRAIL, ONE NAME, AND THE CHIP ON THE TITLE'S OWN LINE.

        The bar above this already reads Design & Quote / The Halvorsens, so
        the underlined DESIGN & QUOTE over the title was the trail said twice.
        The customer was said twice as well: as the heading, and again as the
        first half of the line under it. And the chip floated at the far right
        of the block, vertically centered against three lines of text, aligned
        to none of them - the same thing that was wrong with All jobs on the
        home screen and Open the design on the job page.
      */}
      {/*
        ONE HEAD FOR THE WHOLE RECORD. The overview used to draw its own, so
        merging the two would have put two names, two addresses and two status
        chips on one screen. The record's head is the one that survived: it
        carries the document number and the saved date, which the other did
        not, and the commercial state of the job, which is the question a
        person opens a house to ask. The scope's own draft-or-frozen chip
        stays on step 05, where it is what the step is about.
      */}
      <div className="ws-head">
        <div>
          <h1>{who}</h1>
          <p>
            {[job.address.split('\n')[0], number, `Saved ${longDate(job.savedAt.slice(0, 10))}`]
              .filter(Boolean)
              .join('  ·  ')}
          </p>
        </div>
        <div className="ws-head-acts">
          <span className={`ws-pill ${pillTone(job.status)}`}>{statusLabel(job.status)}</span>
          <button
            className="btn primary"
            onClick={() => open(onOpenDesign)}
            disabled={!doc || !doc.photo}
            title={doc && !doc.photo ? 'This job lost its photograph' : 'Open the design'}
          >
            Open the design
          </button>
        </div>
      </div>

      <div className="steps" role="tablist" aria-label="This job">
        {STEPS.map((s) => (
          <button
            key={s.id}
            role="tab"
            aria-selected={step === s.id}
            className={step === s.id ? 'active' : ''}
            onClick={() => setStep(s.id)}
          >
            {s.n && <small>{s.n}</small>}
            {s.label}
          </button>
        ))}
      </div>

      {/*
        The record itself, chromeless: this file drew the head already, and
        the two used to be separate screens that each drew one.
      */}
      {step === 'overview' && (
        <JobOverview
          job={job}
          chromeless
          onBack={onBack}
          onChanged={onChanged}
          onOpenDesign={onOpenDesign}
          onOpenQuote={onOpenQuote}
        />
      )}


      {missing && <Empty kind="wrong">This job could not be opened on this device.</Empty>}


      {step === 'roof' && (
        <div className="dq-grid">
          <div className="card pad">
            <Sections stats={[]} />
          </div>
          <div className="card pad dq-side">
            <p className="ws-eyebrow">Where this job stands</p>
            <p>
              Every section carries its own measurement, run, materials and price from here
              through to the paperwork.
            </p>
            <div className="dq-stats">
              <div>
                <span className="metric">
                  {totals.feet}
                  <small> ft</small>
                </span>
                <p>Included length</p>
              </div>
              <div>
                <span className="metric">{bulbs}</span>
                <p>Calculated bulbs</p>
              </div>
              <div>
                <span className="metric">{totals.included}</span>
                <p>Sections</p>
              </div>
            </div>
            <div className="next-row">
              <p>
                {totals.unverifiedFeet > 0
                  ? `${Math.round(totals.unverifiedFeet)} ft still needs a site check.`
                  : totals.included === 0
                    ? 'Add a section for each part of the roof.'
                    : 'Every included length was measured at the house.'}
              </p>
              <button className="primary" onClick={() => setStep('mockup')}>
                Review mockup &rarr;
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'mockup' && (
        <div className="dq-grid">
          <div className="card pad">
            <h2>The same runs, carried forward.</h2>
            <p>
              Each run keeps its section, spacing and color. The finished customer image is drawn
              by the real renderer, which is where the night grade and the bulb look live.
            </p>
            {/*
              The runs are picked in the inspector beside this, so listing them
              here as well is the same control twice, a hand's width apart.
            */}
            <div className="next-row">
              <button onClick={() => open(onOpenDesign)} disabled={!doc || !doc.photo}>
                Open the editor
              </button>
              <button className="primary" onClick={() => setStep('materials')}>
                Review materials &rarr;
              </button>
            </div>
          </div>
          <div className="card pad dq-side">
            <RunInspector />
          </div>
        </div>
      )}

      {step === 'materials' && (
        <div className="dq-grid">
          <div className="card pad">
            <Materials />
          </div>
          <div className="card pad dq-side">
            <Packages />
            <div className="next-row">
              <p />
              <button className="primary" onClick={() => setStep('estimate')}>
                Review estimate &rarr;
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'estimate' && (
        <>
          <Estimate />
          <div className="card pad">
            <div className="next-row">
              <p>
                The quote document the homeowner receives is unchanged: the same lines, the same
                ladder, the same printed sheet.
              </p>
              <button onClick={() => open(onOpenQuote)} disabled={!doc}>
                Open the quote
              </button>
              <button className="primary" onClick={() => setStep('scope')}>
                Review approved scope &rarr;
              </button>
            </div>
          </div>
        </>
      )}

      {step === 'scope' && (
        <div className="card pad">
          <Approval />
        </div>
      )}
    </>
  )
}
